import React, { useCallback, useId } from 'react';
import { ChangeHandler } from 'react-hook-form';

import useTheme from '@rover/kibble/themes/useTheme';

import { Flex, Text } from '../../../core';
import { PrimaryLabel, SecondaryLabel } from '../sharedTypography';
import { getAriaDescribedByString, preventDefault } from '../utils';

import { SwitchFieldCommonProps } from './SwitchField.common';
import SwitchFieldIcon from './SwitchFieldIcon';

/** Props for web SwitchField component */
export type Props = SwitchFieldCommonProps & {
  /** ID of element controlled by this switch (for aria-controls) */
  expandableElementId?: string;
  /** Override input element ID */
  id?: string;
};

/**
 * SwitchField is a form field for boolean on/off toggles.
 * Not marked as required since both states are valid.
 * Integrates with react-hook-form via useKibbleForm's `configureField()`.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/4011229795/SwitchField
 */
const SwitchField: React.FC<Props> = ({
  controlSide = 'right',
  controlAlignment = 'center',
  disabled = false,
  expandableElementId,
  fieldProps,
  id,
  isExpanded, // Use this if the switch controls the expansion/collapse of another element
  onChange = () => {},
  primaryLabel,
  primaryLabelA11yHidden,
  secondaryLabel,
  useBoldPrimaryLabel = false,
  primaryLabelSize,
}: Props): JSX.Element => {
  let inputId = useId();
  inputId = id || inputId;
  const secondaryLabelId = useId();
  const theme = useTheme();
  const { background, colors } = theme;

  const isControlSideRight = controlSide === 'right';

  const handleChange = useCallback<ChangeHandler>(
    async (e) => {
      fieldProps.onChange(e);
      onChange(e.target.checked);
    },
    [fieldProps, onChange]
  );

  const handleWrapperClick = useCallback((e: React.MouseEvent) => {
    // pass click to the input to update the form state
    // In React its probably more correct to do this with a ref,
    // but fieldProps already has a ref and we don't want to mess with it
    e.currentTarget.querySelector('input')?.click();
  }, []);

  // error handling if isExpanded and expandableElementId are not both set
  if (
    (isExpanded !== undefined && expandableElementId === undefined) ||
    (isExpanded === undefined && expandableElementId !== undefined)
  ) {
    // eslint-disable-next-line no-console
    console.error(
      'SwitchField: isExpanded and expandableElementId must both be set or both be undefined'
    );
    return <Text textColor="error">SwitchField incorrectly configured</Text>;
  }

  const labelOrder = isControlSideRight ? 0 : 1;
  const iconOrder = isControlSideRight ? 1 : 0;
  const labelPadding = isControlSideRight ? '0x' : '3x';

  return (
    <Flex
      data-testid="kff-switchfield-outer-wrapper"
      onClick={handleWrapperClick}
      flexDirection="row"
      justifyContent="space-between"
      py="2x"
      borderRadius="secondary"
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        alignItems: controlAlignment,
        '&:hover': {
          background: background.interactive.primary_pressed,

          // hover styles for SwitchFieldIcon, we do this here so they activate on hover of the whole field
          input: {
            // unchecked styles first
            borderColor: disabled
              ? colors.interactive_border.disabled
              : colors.interactive_border.primary_pressed,

            // targets the circle
            '& + div': {
              background: disabled
                ? colors.interactive_text.disabled
                : background.interactive.primary_active_pressed,
            },

            // checked styles
            '&:checked': {
              background: disabled
                ? background.interactive.disabled
                : background.interactive.primary_active_pressed,
              borderColor: disabled
                ? colors.interactive_border.disabled
                : colors.interactive_border.primary_active_pressed,

              // targets the circle
              '& + div': {
                background: disabled ? colors.interactive_text.disabled : background.primary,

                // the first svg is the checkmark
                '& > svg': {
                  fill: disabled ? background.interactive.disabled : colors.text.primary,
                },
              },
            },
          },
        },

        // focus outline for the whole row when the input is focused, should only show up for keyboard navigation
        '&:has(input:focus-visible)': {
          outline: '2px solid blue',
        },
      }}
    >
      <Flex
        flex="1"
        flexDirection="column"
        pl={labelPadding}
        justifyContent="center"
        data-testid="kff-switchfield-label-wrapper"
        sx={{ order: labelOrder }}
      >
        <PrimaryLabel
          primaryLabel={primaryLabel}
          primaryLabelA11yHidden={primaryLabelA11yHidden}
          webInputId={inputId}
          required={false}
          sx={{ cursor: 'inherit' }} // match the cursor style of the parent
          variant={useBoldPrimaryLabel ? 'default' : 'option-label'}
          primaryLabelSize={primaryLabelSize}
          // suppress default label click behavior, let handleWrapperClick deal with it
          // avoids some edge cases with showing the focus outline on the wrapper
          onClick={preventDefault}
        />
        <SecondaryLabel labelId={secondaryLabelId} secondaryLabel={secondaryLabel} />
      </Flex>
      <Flex webAs="span" sx={{ order: iconOrder }}>
        <SwitchFieldIcon
          fieldProps={{
            ...fieldProps,
            onChange: handleChange,
            id: inputId,
            'aria-describedby': getAriaDescribedByString([[secondaryLabel, secondaryLabelId]]),
            'aria-expanded': isExpanded,
            'aria-controls': expandableElementId,
          }}
          disabled={disabled}
        />
      </Flex>
    </Flex>
  );
};

export default SwitchField;
